// The morning brief's note (P4-05), parsed for the panel.
//
// `briefs.body_markdown` is now a short note Scout wrote from the day's facts: a line, a few
// bullets, and account names as in-app links, `[Name](/execution?account=<id>)` (P4-01's format).
// `jobs/write_briefs.py` refuses a note that links anything else, and this parser only turns an
// account link into an account: any other link renders as its label, never as an href.
//
// Built on `lib/markdown.ts`, so it parses to data and the component escapes every string. Pure,
// and imported by `node --test`.

import type { MarkdownSpan } from './markdown.ts';
import { parseMarkdown } from './markdown.ts';

export interface NoteSegment {
  text: string;
  bold: boolean;
  // The Salesforce Account id when this segment is an account link, else null.
  accountId: string | null;
}

export type NoteBlock =
  | { kind: 'paragraph'; segments: NoteSegment[] }
  | { kind: 'bullet'; items: NoteSegment[][] }
  | { kind: 'heading'; segments: NoteSegment[] };

const ACCOUNT_LINK_PREFIX = '/execution?account=';
// A Salesforce Account id: `001`, then 12 characters, then the optional 3-character checksum.
const ACCOUNT_ID = /^001[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/;
const MARKDOWN_LINK = /\[([^\]]*)\]\(([^)]*)\)/g;

/** The account id an in-app account link names, or null for any other href. */
export function accountIdFromHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed.startsWith(ACCOUNT_LINK_PREFIX)) return null;
  const accountId = trimmed.slice(ACCOUNT_LINK_PREFIX.length);
  return ACCOUNT_ID.test(accountId) ? accountId : null;
}

/** One span split at its links: plain text, then each link as its label (an account when it is one). */
export function segmentsOf(span: MarkdownSpan): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let cursor = 0;
  for (const match of span.text.matchAll(MARKDOWN_LINK)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ text: span.text.slice(cursor, start), bold: span.bold, accountId: null });
    segments.push({ text: match[1] ?? '', bold: span.bold, accountId: accountIdFromHref(match[2] ?? '') });
    cursor = start + match[0].length;
  }
  if (cursor < span.text.length) segments.push({ text: span.text.slice(cursor), bold: span.bold, accountId: null });
  return segments;
}

function segmentsOfSpans(spans: MarkdownSpan[]): NoteSegment[] {
  const segments: NoteSegment[] = [];
  for (const span of spans) segments.push(...segmentsOf(span));
  return segments;
}

// `[**Acme**](…)`: the bold markers would split the link before it is seen, so they move outside it.
const BOLD_LINK = /\[\*\*([^\]]*?)\*\*\]\(([^)]*)\)/g;

/** The note as blocks. A top '# Morning brief' title is dropped: the panel already has one. */
export function parseBriefNote(body: string): NoteBlock[] {
  const blocks: NoteBlock[] = [];
  const parsed = parseMarkdown((body || '').replace(BOLD_LINK, '**[$1]($2)**'));
  for (const [index, block] of parsed.entries()) {
    if (block.kind === 'heading') {
      if (index === 0 && block.level === 1) continue;
      blocks.push({ kind: 'heading', segments: segmentsOfSpans(block.spans) });
    } else if (block.kind === 'bullet') {
      blocks.push({ kind: 'bullet', items: block.items.map(segmentsOfSpans) });
    } else {
      blocks.push({ kind: 'paragraph', segments: segmentsOfSpans(block.spans) });
    }
  }
  return blocks;
}
